import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, User, AlertCircle } from "lucide-react";
import { useForm } from "react-hook-form";
import { useCoreController } from "@/services/coreController";
import { appApi } from "@/services/api";
import { analyticsService } from "@/services/analytics";
import { storage } from "@/lib/storage";
import { Notification } from "@/components/Notification";
import { BonusSummaryCard } from "@/components/BonusSummaryCard";
import { calculateBonus, calculateTotalWithBonus } from "@/constants/theme";

interface DepositPageState {
  type: string;
  amount: number;
  accountType: string;
  commercialId?: string;
}

interface DepositForm {
  user_id: string;
}

export function DepositPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const coreController = useCoreController();
  const state = location.state as DepositPageState;

  const [isLoading, setIsLoading] = useState(false);
  const [notification, setNotification] = useState<{
    isVisible: boolean;
    message: string;
    type: "error" | "success";
  }>({
    isVisible: false,
    message: "",
    type: "error",
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<DepositForm>();

  useEffect(() => {
    // Track screen view
    analyticsService.trackScreenView("deposit_page");

    // Load saved user ID from storage
    const savedUserId = storage.read<string>(
      `user_id_${coreController.compte.type}`
    );
    if (savedUserId) {
      setValue("user_id", savedUserId);
    }

    // Redirect if no state
    if (!state || !state.amount) {
      navigate("/");
    }
  }, []);

  const getThemeColor = (): string => {
    switch (coreController.compte.type) {
      case "1XBET":
        return "#002366";
      case "BETWINNER":
        return "#0052cc";
      default:
        return "#0052cc";
    }
  };

  const themeColor = getThemeColor();

  const showNotification = (
    message: string,
    type: "error" | "success" = "error"
  ) => {
    setNotification({
      isVisible: true,
      message,
      type,
    });
  };

  const hideNotification = () => {
    setNotification((prev) => ({ ...prev, isVisible: false }));
  };

  const onSubmit = async (data: DepositForm) => {
    setIsLoading(true);

    try {
      const userId = data.user_id;
      const accountId = coreController.compte.id;
      const amount = state.amount;

      // Save user ID to storage
      storage.write(`user_id_${coreController.compte.type}`, userId);

      // Track deposit initiation
      await analyticsService.trackDepositInitiated({
        amount,
        accountType: coreController.compte.type || "",
        commercialId: coreController.commercialId || undefined,
      });

      // Verify account ID and get user name
      const userName = await appApi.getUserName(userId, accountId);

      if (userName === null) {
        showNotification(
          `Votre ID ${coreController.compte.type} semble invalide. Veuillez vérifier l'identifiant ${coreController.compte.type} saisi.`
        );

        analyticsService.trackError(
          new Error("Invalid account ID"),
          "deposit_account_verification"
        );
        return;
      }
      if (userName.trim() === "") {
        showNotification(
          `Avertissement : Aucun nom associé à votre ID ${coreController.compte.type}.`
        );
      }


      const depositFactor = coreController.compte.deposit_bonus_factor ?? 1;
      const bonusAmount = depositFactor > 1 ? calculateBonus(amount, "deposit", depositFactor) : 0;
      const totalWithBonusAmount = depositFactor > 1 ? calculateTotalWithBonus(amount, "deposit", depositFactor) : amount;

      // Navigate to payment screen with user info
      navigate("/payment", {
        state: {
          type: "deposit",
          amount,
          accountType: coreController.compte.type,
          userId,
          userName,
          accountId,
          commercialId: state.commercialId || coreController.commercialId,
          bonus: bonusAmount,
          totalWithBonus: totalWithBonusAmount,
        },
      });
    } catch (error: any) {
      console.error("Error during deposit:", error);
   showNotification(
          `Votre ID ${coreController.compte.type} semble invalide. Veuillez vérifier l'identifiant ${coreController.compte.type} saisi.`
        );
      analyticsService.trackError(error, "deposit_page_verification");
    } finally {
      setIsLoading(false);
    }
  };

  if (!state) {
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className="min-h-screen bg-background"
    >
      {/* App Bar */}
      <div className="bg-card shadow-sm sticky top-0 z-10">
        <div className="max-w-md mx-auto px-4 py-4 flex items-center">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-full bg-card shadow-md hover:shadow-lg transition-shadow ring-1 ring-border"
            style={{ color: themeColor }}
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1
            className="flex-1 text-center text-lg font-semibold"
            style={{ color: themeColor }}
          >
            Recharge {coreController.compte.type}
          </h1>
          <div className="w-9" /> {/* Spacer */}
        </div>
      </div>

      {/* Body */}
      <div className="max-w-md mx-auto px-5 py-6">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Bonus Summary Card */}
          <BonusSummaryCard
            amount={state.amount}
            type="deposit"
            brandName="EASYSERVICE"
            accentColor={themeColor}
            bonusFactor={coreController.compte.deposit_bonus_factor}
          />

          {/* Account ID Field */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="space-y-3"
          >
            <label className="text-base font-semibold text-foreground">
              Information du compte
            </label>
            <div className="relative">
              <div
                className="absolute left-4 top-1/2 -translate-y-1/2 p-2 rounded-lg"
                style={{ backgroundColor: `${themeColor}1A` }}
              >
                <User className="w-5 h-5" style={{ color: themeColor }} />
              </div>
              <input
                {...register("user_id", {
                  required: "Ce champ est requis",
                  minLength: {
                    value: 3,
                    message:
                      "L'ID du compte doit comporter au moins 3 caractères",
                  },
                  pattern: {
                    value: /^[0-9]+$/,
                    message: "L'ID du compte doit contenir que des chiffres",
                  },
                })}
                type="text"
                inputMode="numeric"
                placeholder="Entrez votre ID de compte"
                className={`w-full pl-16 pr-4 py-4 rounded-xl border-2 transition-all bg-surface text-base text-foreground placeholder:text-muted-foreground ${
                  errors.user_id
                    ? "border-red-500 focus:border-red-500"
                    : "border-input focus:border-[var(--theme-color)]"
                }`}
                style={
                  !errors.user_id
                    ? ({ "--theme-color": themeColor } as any)
                    : undefined
                }
              />
            </div>
            {errors.user_id && (
              <p className="text-sm text-destructive flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.user_id.message}
              </p>
            )}
          </motion.div>

          {/* Warning Card */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="rounded-xl p-4 bg-destructive/10 border border-destructive/30"
          >
            <div className="flex gap-3">
              <AlertCircle className="w-6 h-6 text-destructive flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-destructive mb-2">
                  Information importante
                </h3>
                <p className="text-xs text-destructive/90 leading-relaxed">
                  En cas d'erreur sur le numéro de compte{" "}
                  {coreController.compte.type}, aucun remboursement ne pourra
                  être effectué. Veuillez vérifier attentivement votre ID de
                  compte et votre nom dans la description de la page suivante.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Spacer */}
          <div className="flex-1 min-h-[2rem]" />

          {/* Continue Button */}
          <motion.button
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            type="submit"
            disabled={isLoading}
            className="w-full py-4 rounded-xl text-white font-semibold text-base flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ backgroundColor: themeColor }}
          >
            {isLoading ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <span>Continuer</span>
                <ArrowRight className="w-5 h-5" />
              </>
            )}
          </motion.button>
        </form>
      </div>

      {/* Notification */}
      <Notification
        message={notification.message}
        type={notification.type}
        isVisible={notification.isVisible}
        onClose={hideNotification}
      />
    </motion.div>
  );
}

export default DepositPage;
